import { defaultBusiness as NAP } from "@/lib/site-config";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { CTABlock } from "@/components/CTABlock";
import { AnimatedSection, StaggerContainer, StaggerItem } from "@/components/AnimatedSection";
import { Calendar, Clock, ArrowRight, Tag, Loader2 } from "lucide-react";
import { faqSchema } from "@/components/FAQAccordion";
import { seoHead } from "@/lib/seo";
import { listBlogPosts } from "@/lib/cms.functions";

const blogFaqs = [
  { q: "Where is Woodseer Autos located?", a: "We're at 16 Pixley Street, London E14 7DF. Pixley Street is the registered/main address — vehicles should enter via the gated yard at 14 Copenhagen Place, E14 7DF." },
  { q: "What services do you offer?", a: "MOT testing (Classes 1–5 & 7), car servicing, diagnostics, mechanical repairs and bodywork." },
  { q: "Do I need to book in advance?", a: `We recommend calling ahead on ${NAP.phone}, but we welcome walk-ins for quick checks.` },
];

type Post = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  cover_image_url: string | null;
  tags: string[];
  published_at: string | null;
  reading_minutes: number | null;
};

export const Route = createFileRoute("/blog/")({
  loader: async () => {
    try {
      const r = await listBlogPosts();
      return { posts: (r.items ?? []) as Post[] };
    } catch { return { posts: [] as Post[] }; }
  },
  head: () => seoHead({
    path: "/blog",
    title: "Car Care Blog | Woodseer Autos, East London",
    description: "MOT tips, warning light guides and servicing advice from an independent East London garage. Woodseer Autos, E14.",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Blog", path: "/blog" },
    ],
    jsonLd: [
      faqSchema(blogFaqs),
      {
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "Woodseer Autos Blog",
        description: "Practical car care advice from an independent East London garage.",
        url: "https://woodseerautos.co.uk/blog",
        publisher: {
          "@type": "Organization",
          name: "Woodseer Autos",
          url: "https://woodseerautos.co.uk",
        },
      },
    ],
  }),
  component: BlogIndexPage,
});

function BlogIndexPage() {
  const { posts: initial } = Route.useLoaderData();
  const [posts, setPosts] = useState<Post[]>(initial);
  const [loading, setLoading] = useState(initial.length === 0);

  useEffect(() => {
    if (initial.length > 0) return;
    listBlogPosts()
      .then((r) => setPosts((r.items ?? []) as Post[]))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [initial.length]);

  const categories = [...new Set(posts.flatMap((p) => p.tags ?? []).slice(0, 12))];

  return (
    <>
      <section className="section-y">
        <div className="mx-auto max-w-4xl px-4 md:px-6 text-center">
          <p className="eyebrow">Workshop journal</p>
          <h1 className="mt-4 display-xl text-foreground">Practical car care advice — from real mechanics.</h1>
          <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            Honest answers, useful guides and warning-light explainers from our workshop in East London. No fluff — just the stuff you actually need to know.
          </p>
        </div>
      </section>

      <section className="pb-20 md:pb-24">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : posts.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">No articles published yet — check back soon.</p>
          ) : (
            <>
              {categories.length > 0 && (
                <div className="flex flex-wrap justify-center gap-2 mb-12">
                  {categories.map((cat) => (
                    <span
                      key={cat}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-card border border-border text-[11px] font-bold uppercase tracking-[0.14em] text-foreground/75"
                    >
                      <Tag className="h-3 w-3 text-primary" />
                      {cat}
                    </span>
                  ))}
                </div>
              )}

              <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {posts.map((post) => {
                  const category = post.tags?.[0] ?? "Article";
                  return (
                    <StaggerItem key={post.slug}>
                      <Link
                        to="/blog/$slug"
                        params={{ slug: post.slug }}
                        className="group flex flex-col rounded-2xl surface-premium surface-premium-hover p-7 h-full"
                      >
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.18em] uppercase text-primary bg-primary/8 border border-primary/15 px-2.5 py-1 rounded-full w-fit">
                          {category}
                        </span>
                        <h2 className="mt-4 text-lg font-bold text-foreground group-hover:text-primary transition-colors leading-snug tracking-tight">
                          {post.title}
                        </h2>
                        {post.excerpt && (
                          <p className="mt-2 text-sm text-muted-foreground leading-relaxed flex-1">
                            {post.excerpt}
                          </p>
                        )}
                        <div className="mt-5 pt-4 border-t border-border/60 flex items-center justify-between">
                          <div className="flex items-center gap-3 text-[11px] text-muted-foreground font-medium">
                            {post.published_at && (
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {new Date(post.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                              </span>
                            )}
                            {post.reading_minutes && (
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {post.reading_minutes} min
                              </span>
                            )}
                          </div>
                          <ArrowRight className="h-4 w-4 text-primary opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                        </div>
                      </Link>
                    </StaggerItem>
                  );
                })}
              </StaggerContainer>
            </>
          )}
        </div>
      </section>

      <CTABlock headline="Need help with your vehicle?" subtext="Whether it's an MOT, a service or a warning light — give us a call and we'll sort it." />
    </>
  );
}
